import React from "react"
import PropTypes from "prop-types"
import { createRestaurantOrder } from '../actions/restaurant';
import { cartCount, getRestaurantId, setRestaurantId, getState, saveState, clearCart } from '../actions/storage';
import { colors } from '../lib/utilities'
import ItemMenue from './ItemMenue'
import CartDetails from './CartDetails'

const SLATE = '#1a1a1a'
const SLATE_DEEP = '#0f0f0f'
const CHALK = '#f5f0e1'
const CREAM = '#e8d8a8'
const ACCENT = '#d4b86a'
const MUTED = '#9a9485'

const SERIF = "'Georgia', 'Times New Roman', serif"
const SANS = "'Helvetica Neue', Arial, sans-serif"

const pageBackground = (src) => (
  src
    ? `linear-gradient(rgba(15,15,15,0.55), rgba(15,15,15,0.78)), url(${src})`
    : `radial-gradient(ellipse at center, ${SLATE} 0%, ${SLATE_DEEP} 100%)`
)

let styles = {
  page: {
    height: '100vh',
    overflow: 'auto',
    background: `radial-gradient(ellipse at center, ${SLATE} 0%, ${SLATE_DEEP} 100%)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
    color: CHALK,
    fontFamily: SANS,
    paddingBottom: 80,
  },

  topNav: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: SLATE_DEEP,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid rgba(245,240,225,0.08)',
  },

  navTabsScroller: {
    flex: 1,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  },

  navTab: {
    display: 'inline-block',
    padding: '6px 0',
    margin: '0 12px',
    color: MUTED,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
  },

  navTabActive: {
    color: CHALK,
    borderBottom: `2px solid ${ACCENT}`,
  },

  navTabBrand: {
    color: ACCENT,
    fontWeight: 700,
    marginRight: 8,
    fontFamily: SERIF,
    fontStyle: 'italic',
    fontSize: 16,
    letterSpacing: 0.5,
    textTransform: 'none',
  },

  navIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    color: CHALK,
    fontSize: 16,
    marginLeft: 12,
  },

  navIcon: {
    color: CHALK,
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontSize: 16,
    cursor: 'pointer',
  },

  menuTitleWrap: {
    textAlign: 'center',
    padding: '40px 20px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 18,
  },

  menuTitleLine: {
    flex: 1,
    maxWidth: 80,
    height: 1,
    background: CHALK,
    opacity: 0.7,
  },

  menuTitle: {
    fontFamily: SERIF,
    fontStyle: 'italic',
    color: CHALK,
    fontSize: 44,
    fontWeight: 400,
    letterSpacing: 2,
    textTransform: 'uppercase',
    margin: 0,
    lineHeight: 1.1,
  },

  menuSubtitle: {
    textAlign: 'center',
    color: MUTED,
    fontSize: 12,
    letterSpacing: 4,
    textTransform: 'uppercase',
    marginTop: 4,
    marginBottom: 32,
  },

  categorySection: {
    maxWidth: 920,
    margin: '0 auto',
    padding: '28px 24px',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 24,
  },

  categorySectionWithImages: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 220px)',
    alignItems: 'start',
  },

  categorySectionReversed: {
    gridTemplateColumns: 'minmax(0, 220px) minmax(0, 1fr)',
  },

  categoryHeader: {
    marginBottom: 16,
  },

  categoryTitle: {
    fontFamily: SERIF,
    color: CHALK,
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: 3,
    textTransform: 'uppercase',
    margin: 0,
  },

  categoryUnderline: {
    width: 60,
    height: 2,
    background: ACCENT,
    marginTop: 8,
  },

  itemRow: {
    display: 'flex',
    alignItems: 'baseline',
    padding: '10px 0',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    color: CHALK,
  },

  itemRowName: {
    color: CHALK,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 2,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '60%',
  },

  itemRowDots: {
    flex: 1,
    margin: '0 8px',
    borderBottom: '2px dotted rgba(245,240,225,0.25)',
    transform: 'translateY(-4px)',
  },

  itemRowPrice: {
    color: CREAM,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 1,
    whiteSpace: 'nowrap',
  },

  itemRowDescription: {
    color: MUTED,
    fontSize: 12,
    fontStyle: 'italic',
    paddingLeft: 2,
    paddingBottom: 8,
    marginTop: -4,
  },

  imagesColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
    alignItems: 'center',
    justifyItems: 'center',
  },

  imagesColumnSingle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  circleImage: {
    width: 100,
    height: 100,
    borderRadius: '50%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    boxShadow: '0 8px 18px rgba(0,0,0,0.55)',
  },

  circleImageLarge: {
    width: 150,
    height: 150,
  },

  taccBadge: {
    height: 22,
    marginLeft: 6,
    verticalAlign: 'middle',
  },

  loadingWrap: {
    minHeight: '100vh',
    background: SLATE_DEEP,
    color: CHALK,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    fontFamily: SERIF,
    fontStyle: 'italic',
  },
}

class Restaurant extends React.Component {
  state = {
    restaurantDetails: null,
    loading: true,
    orderType: 'TakeAway',
    cartItemsCount: 0,
    activeCategoryId: null,
  }

  componentDidMount() {
    this.loadData()
  }

  scrollToCategory(categoryId) {
    const el = document.getElementById(`category-${categoryId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    this.setState({ activeCategoryId: categoryId })
  }

  formatPrice(price) {
    return new Intl.NumberFormat("es-AR", { style: 'currency', currency: 'ARS' }).format(price)
  }

  itemPrice(menu) {
    if (menu.use_portion_price && menu.available_portions && menu.available_portions.length > 0) {
      return this.formatPrice(menu.available_portions[0].price)
    }
    if (menu.price > 0) return this.formatPrice(menu.price)
    return ''
  }

  async loadData() {
    setRestaurantId(this.props.restaurantId)
    clearCart()

    await this.setState({ loading: true })
    let newState = { cartItemsCount: 0 }
    try {
      let restaurantDetails = this.props.restaurantData
      newState.restaurantDetails = restaurantDetails
      const firstCategory = restaurantDetails && restaurantDetails.categories && restaurantDetails.categories[0]
      if (firstCategory) newState.activeCategoryId = firstCategory.id
    } finally {
      newState.loading = false
      this.setState(newState)
      saveState(newState)
    }
  }

  onItemAdded() {
    this.setState({ cartItemsCount: cartCount() })
  }

  onItemChanged() {
    this.setState({ cartItemsCount: cartCount() })
  }

  renderTopNav(restaurantDetails) {
    const { activeCategoryId } = this.state
    const categories = restaurantDetails.categories || []

    return (
      <nav style={styles.topNav}>
        <div style={styles.navTabsScroller}>
          {categories.map(category => {
            const isActive = activeCategoryId === category.id
            return (
              <span
                key={`tab-${category.id}`}
                style={{ ...styles.navTab, ...(isActive ? styles.navTabActive : {}) }}
                onClick={() => this.scrollToCategory(category.id)}
              >
                {category.name}
              </span>
            )
          })}
        </div>
        <div style={styles.navIcons}>
          <CartDetails
            cartItemsCount={this.state.cartItemsCount}
            onItemChanged={() => this.onItemChanged()}
          />
        </div>
      </nav>
    )
  }

  renderMenuTitle(restaurantDetails) {
    const title = (restaurantDetails.name || 'Menu').toUpperCase()
    return (
      <div>
        <div style={styles.menuTitleWrap}>
          <span style={styles.menuTitleLine} />
          <h1 style={styles.menuTitle}>{title}</h1>
          <span style={styles.menuTitleLine} />
        </div>
        <div style={styles.menuSubtitle}>— carta —</div>
      </div>
    )
  }

  renderItemRow(menu, category) {
    return (
      <div key={`menu-${menu.id}`}>
        <button
          type="button"
          style={styles.itemRow}
          data-toggle="modal"
          data-target={`#item-menu-${menu.id}`}
        >
          <span style={styles.itemRowName}>
            {menu.name}
            {menu.tacc_free && (
              <img src={this.props.imgSinTaccSrc} style={styles.taccBadge} alt="sin TACC" />
            )}
          </span>
          <span style={styles.itemRowDots} />
          <span style={styles.itemRowPrice}>{this.itemPrice(menu)}</span>
        </button>
        {menu.description && (
          <div style={styles.itemRowDescription}>{menu.description}</div>
        )}
      </div>
    )
  }

  renderCategoryImages(menues) {
    const withImages = menues.filter(m => m.image_thumb || m.image_original).slice(0, 1)
    if (withImages.length === 0) return null

   
      const m = withImages[0]
      return (
        <div style={styles.imagesColumnSingle}>
          <div
            style={{
              ...styles.circleImage,
              ...styles.circleImageLarge,
              backgroundImage: `url(${m.image_thumb || m.image_original})`,
            }}
          />
        </div>
      )
  }

  renderCategory(category, index, restaurantImage) {
    const menues = (category.menues || []).slice().sort((a, b) => (a.order_number || 0) - (b.order_number || 0))
    const hasImages = menues.some(m => m.image_thumb || m.image_original)
    const reversed = index % 2 === 1

    const sectionStyle = {
      ...styles.categorySection,
      ...(hasImages ? styles.categorySectionWithImages : {}),
      ...(hasImages && reversed ? styles.categorySectionReversed : {}),
    }

    const list = (
      <div>
        <div style={styles.categoryHeader}>
          <h2 style={styles.categoryTitle}>{category.name}</h2>
          <div style={styles.categoryUnderline} />
        </div>
        <div>
          {menues.map(menu => this.renderItemRow(menu, category))}
        </div>
      </div>
    )

    const images = hasImages ? this.renderCategoryImages(menues) : null

    return (
      <section
        key={`category-${category.id}`}
        id={`category-${category.id}`}
        style={sectionStyle}
      >
        {hasImages && reversed ? (
          <>
            {images}
            {list}
          </>
        ) : (
          <>
            {list}
            {images}
          </>
        )}

        {menues.map(menu => (
          <ItemMenue
            key={`modal-${menu.id}`}
            menueId={menu.id}
            imgSinTaccSrc={this.props.imgSinTaccSrc}
            categoryName={category.name}
            restaurantImage={restaurantImage}
            backgroundSrc={this.props.backgroundSrc}
            onItemAdded={() => this.onItemAdded()}
          />
        ))}
      </section>
    )
  }

  render() {
    if (this.state.loading) {
      return <div style={styles.loadingWrap}>Cargando...</div>
    }

    const { restaurantDetails } = this.state

    if (!restaurantDetails) {
      return <div style={styles.loadingWrap}>Error al cargar el restaurante...</div>
    }

    const pageStyle = {
      ...styles.page,
      background: pageBackground(this.props.backgroundSrc),
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat',
    }

    return (
      <div style={pageStyle}>
        
        {this.renderMenuTitle(restaurantDetails)}
        {this.renderTopNav(restaurantDetails)}
        {restaurantDetails.categories.map((category, idx) =>
          this.renderCategory(category, idx, restaurantDetails.image_original)
        )}
      </div>
    )
  }
}

Restaurant.propTypes = {
  restaurantId: PropTypes.string,
  store: PropTypes.string,
  imgSrc: PropTypes.string,
  imgIosSrc: PropTypes.string,
  imgAndroidSrc: PropTypes.string,
  dotIconSrc: PropTypes.string,
  ddsIconSrc: PropTypes.string,
  imgSinTaccSrc: PropTypes.string,
  backgroundSrc: PropTypes.string,
  restaurantData: PropTypes.object
};

export default Restaurant
